import React, { useEffect, useState, Component } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, ExternalLink, MessageSquare, Send, Star, X } from 'lucide-react';
const TRUSTPILOT_REVIEW_URL = 'https://www.trustpilot.com/review/toxicdetector.com';
interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  milestone?: number | null;
}
export function FeedbackModal({
  isOpen,
  onClose,
  milestone = null
}: FeedbackModalProps) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [privateFeedbackSent, setPrivateFeedbackSent] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    setRating(0);
    setHoverRating(0);
    setFeedbackText('');
    setPrivateFeedbackSent(false);
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const handlePrivateFeedback = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const subject = encodeURIComponent('Private ToxicDetector feedback');
    const body = encodeURIComponent(`Rating: ${rating}/5\n${milestone ? `Scan milestone: ${milestone}\n` : ''}\n${feedbackText}`);
    window.location.href = `mailto:support@toxicdetector.com?subject=${subject}&body=${body}`;
    setPrivateFeedbackSent(true);
  };
  return <AnimatePresence data-id="element-2288">
      {isOpen && <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" data-id="element-2289">
          <motion.button type="button" aria-label="Close feedback modal" className="absolute inset-0 cursor-default bg-black/85 backdrop-blur-sm" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} data-id="element-2290" />

          <motion.section role="dialog" aria-modal="true" aria-labelledby="smart-feedback-title" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} transition={{
        duration: 0.22,
        ease: 'easeOut'
      }} className="relative w-full max-w-lg overflow-hidden border border-red-500/40 bg-[#050505] p-6 shadow-2xl sm:p-8" data-id="element-2291">
            <div className="absolute left-0 top-0 h-1 w-full bg-[#FF0000]" data-id="element-2292" />
            <div className="pointer-events-none absolute left-0 top-0 h-6 w-6 border-l-2 border-t-2 border-[#FF0000]" data-id="element-2293" />
            <div className="pointer-events-none absolute right-0 top-0 h-6 w-6 border-r-2 border-t-2 border-[#FF0000]" data-id="element-2294" />
            <div className="pointer-events-none absolute bottom-0 left-0 h-6 w-6 border-b-2 border-l-2 border-[#FF0000]" data-id="element-2295" />
            <div className="pointer-events-none absolute bottom-0 right-0 h-6 w-6 border-b-2 border-r-2 border-[#FF0000]" data-id="element-2296" />

            <button type="button" onClick={onClose} className="absolute right-4 top-4 rounded p-2 text-gray-500 transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-[#FF0000]" aria-label="Close feedback modal" data-id="element-2297">
              <X size={20} data-id="element-2298" />
            </button>

            <header className="mb-7 pr-10 text-center" data-id="element-2299">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-red-500/40 bg-red-500/10" data-id="element-2300">
                <MessageSquare className="text-[#FF0000]" size={20} data-id="element-2301" />
              </div>
              <p className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF0000]" data-id="element-2302">
                {milestone ? `Scan ${milestone} complete` : 'Share your story'}
              </p>
              <h2 id="smart-feedback-title" className="text-2xl font-black uppercase tracking-tight text-white" data-id="element-2303">
                How was your experience?
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-gray-400" data-id="element-2304">
                Your honest feedback helps us make the experience clearer and
                more useful.
              </p>
            </header>

            <div className="mb-7 text-center" data-id="element-2305">
              <p className="mb-3 font-mono text-xs uppercase tracking-widest text-gray-500" data-id="element-2306">
                Step 1 — choose a rating
              </p>
              <div className="flex justify-center gap-2" role="radiogroup" aria-label="Rate your experience from one to five stars" data-id="element-2307">
                {[1, 2, 3, 4, 5].map(star => {
              const isSelected = star <= (hoverRating || rating);
              return <button key={star} type="button" role="radio" aria-checked={rating === star} aria-label={`${star} star${star === 1 ? '' : 's'}`} onClick={() => setRating(star)} onMouseEnter={() => setHoverRating(star)} onMouseLeave={() => setHoverRating(0)} className="rounded p-1 text-gray-700 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#FF0000]" data-id="element-2308">
                      <Star size={34} strokeWidth={1.5} className={isSelected ? 'fill-[#FFD700] text-[#FFD700]' : 'text-gray-700'} data-id="element-2309" />
                    </button>;
            })}
              </div>
            </div>

            {rating > 0 && <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} className="space-y-4 border-t border-gray-800 pt-6" data-id="element-2310">
                <div data-id="element-2311">
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500" data-id="element-2312">
                    Step 2 — optional private feedback
                  </p>
                  <p className="mt-2 text-sm text-gray-400" data-id="element-2313">
                    Tell us what worked or what we should improve. This opens a
                    private message to our support team.
                  </p>
                </div>

                <form onSubmit={handlePrivateFeedback} className="space-y-3" data-id="element-2314">
                  <label htmlFor="smart-feedback-message" className="sr-only" data-id="element-2315">
                    Private feedback
                  </label>
                  <textarea id="smart-feedback-message" value={feedbackText} onChange={event => setFeedbackText(event.target.value)} rows={4} placeholder="We’re listening — what would you like us to know?" className="w-full resize-none rounded-lg border border-gray-800 bg-[#0A0A0A] p-4 text-sm text-white outline-none transition-colors placeholder:text-gray-600 focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000]" data-id="element-2316" />
                  <button type="submit" disabled={!feedbackText.trim()} className="inline-flex items-center gap-2 text-sm font-bold text-gray-300 transition-colors hover:text-white disabled:cursor-not-allowed disabled:text-gray-600" data-id="element-2317">
                    {privateFeedbackSent ? <CheckCircle2 size={16} data-id="element-2318" /> : <Send size={16} data-id="element-2319" />}
                    {privateFeedbackSent ? 'Private feedback prepared' : 'Send private feedback'}
                  </button>
                </form>

                <div className="border border-[#FFD700]/30 bg-[#FFD700]/5 p-4" data-id="element-2320">
                  <p className="text-sm font-bold text-white" data-id="element-2321">
                    Want to share publicly?
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-gray-400" data-id="element-2322">
                    A Trustpilot review helps other people discover
                    ToxicDetector. Every rating is welcome.
                  </p>
                  <a href={TRUSTPILOT_REVIEW_URL} target="_blank" rel="noopener noreferrer" onClick={onClose} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#FF0000] px-5 py-3 text-sm font-black uppercase tracking-wider text-white transition-colors hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-2323">
                    Share on Trustpilot
                    <ExternalLink size={16} data-id="element-2324" />
                  </a>
                </div>
              </motion.div>}
          </motion.section>
        </div>}
    </AnimatePresence>;
}